import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  FileStack,
  Copy,
  Sparkles,
  FileText,
} from 'lucide-react'
import { FilterConditionsSummaryFromConditions } from '@/components/common/FilterConditionsSummary'
import { scenariosApiClient } from '@/lib/api'
import { pathFor, ROUTE_REGISTRY } from '@/lib/routes'
import { useAppAuth } from '@/contexts/app-auth.context'
import { EmptyState } from '@/components/ui/empty-state'
import { confirmDialog } from '@/lib/utils/window-utils'
import { cn } from '@/lib/utils'
import type { Scenario } from '@super/shared-types'
import { getActiveScenarioTemplates } from '@super/shared-types'

interface CreateScenarioModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called with a template id to seed from, or null to start from scratch. */
  onSelect: (templateId: string | null) => void
}

function CreateScenarioModal({
  open,
  onOpenChange,
  onSelect,
}: CreateScenarioModalProps) {
  const templates = getActiveScenarioTemplates()
  const cardClass = cn(
    'flex flex-col items-start p-4 rounded-lg border-2 border-border text-left transition-all',
    'hover:border-primary hover:bg-accent',
    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>How do you want to start?</DialogTitle>
          <DialogDescription>
            Start from a template with pre-filled criteria, or start from
            scratch.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2 max-h-[60vh] overflow-y-auto">
          {templates.map(template => (
            <button
              key={template.id}
              type="button"
              onClick={() => onSelect(template.id)}
              className={cardClass}
            >
              <div className="flex items-center gap-2 font-medium">
                <Sparkles className="w-4 h-4 text-primary shrink-0" />
                {template.name}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {template.description}
              </div>
            </button>
          ))}
          <button
            type="button"
            onClick={() => onSelect(null)}
            className={cardClass}
          >
            <div className="flex items-center gap-2 font-medium">
              <FileText className="w-4 h-4 text-primary shrink-0" />
              Start from scratch
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Configure everything yourself on a blank scenario
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const SELECTION_CRITERIA_PREVIEW_LEN = 80

function truncateCriteria(text: string | null | undefined): string {
  if (!text?.trim()) return '—'
  const trimmed = text.trim()
  if (trimmed.length <= SELECTION_CRITERIA_PREVIEW_LEN) return trimmed
  return trimmed.slice(0, SELECTION_CRITERIA_PREVIEW_LEN).trim() + '…'
}

export function ScenariosPage() {
  useDocumentHead({
    title: 'Scenarios',
    description:
      'Define scenarios and variables to extract for handoffs and outbound',
  })

  const navigate = useNavigate()
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [loading, setLoading] = useState(true)
  const [createModalOpen, setCreateModalOpen] = useState(false)

  const loadScenarios = async () => {
    if (!currentCompany?.id) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const list = isSuperAdmin
        ? await scenariosApiClient.getScenariosForCompanyByAdmin(
            currentCompany.id,
          )
        : await scenariosApiClient.getScenarios()
      setScenarios(list)
    } catch {
      toast.error('Failed to load scenarios')
      setScenarios([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadScenarios()
  }, [currentCompany?.id, isSuperAdmin])

  const handleCreateNew = () => setCreateModalOpen(true)
  const handleSelectTemplate = (templateId: string | null) => {
    setCreateModalOpen(false)
    const path = ROUTE_REGISTRY.SCENARIOS_NEW.path
    navigate(templateId ? `${path}?template=${templateId}` : path)
  }
  const handleEdit = (s: Scenario) =>
    navigate(pathFor('SCENARIOS_DETAIL', { id: s.id }))

  const handleDuplicate = async (s: Scenario) => {
    if (!currentCompany?.id) return
    const payload = {
      type: s.type,
      name: `${s.name} (Copy)`,
      selectionCriteria: s.selectionCriteria,
      filterConditions: s.filterConditions ?? undefined,
    }
    try {
      const created = isSuperAdmin
        ? await scenariosApiClient.createScenarioForCompany(
            currentCompany.id,
            payload,
          )
        : await scenariosApiClient.createScenario(payload)
      if (created) {
        toast.success('Scenario duplicated')
        loadScenarios()
      } else {
        toast.error('Failed to duplicate scenario')
      }
    } catch {
      toast.error('Failed to duplicate scenario')
    }
  }

  const handleDelete = async (s: Scenario) => {
    if (!confirmDialog(`Are you sure you want to delete "${s.name}"?`)) return
    if (!currentCompany?.id) return
    const ok = isSuperAdmin
      ? await scenariosApiClient.deleteScenarioForCompany(
          s.id,
          currentCompany.id,
        )
      : await scenariosApiClient.deleteScenario(s.id)
    if (ok) {
      toast.success('Scenario deleted')
      setScenarios(prev => prev.filter(sc => sc.id !== s.id))
    } else {
      toast.error('Failed to delete scenario')
    }
  }

  const sortedScenarios = [...scenarios].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 pb-24">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-6">
          <PageHeader title="Scenarios" />
          <Button
            onClick={handleCreateNew}
            variant="form"
            size="sm"
            className="h-9 w-fit shrink-0"
          >
            Create scenario
          </Button>
        </div>

        {loading ? (
          <div className="border rounded-lg">
            <div className="p-8 text-center text-sm text-muted-foreground">
              Loading scenarios…
            </div>
          </div>
        ) : sortedScenarios.length === 0 ? (
          <EmptyState
            icon={<FileStack className="h-8 w-8 text-muted-foreground" />}
            title="No scenarios yet"
            description="Create your first scenario to classify conversations and drive handoffs and outbound."
            actionLabel="Create your first scenario"
            onAction={handleCreateNew}
            actionButtonVariant="default"
          />
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Conditions
                  </TableHead>
                  <TableHead className="w-[100px]" align="center">
                    Options
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedScenarios.map(s => (
                  <TableRow
                    key={s.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleEdit(s)}
                  >
                    <TableCell title={s.selectionCriteria ?? undefined}>
                      <div className="font-medium">{s.name}</div>
                      <div className="text-sm text-muted-foreground truncate max-w-md">
                        {truncateCriteria(s.selectionCriteria)}
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <FilterConditionsSummaryFromConditions
                        filterConditions={s.filterConditions}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={e => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onClick={e => {
                              e.stopPropagation()
                              handleEdit(s)
                            }}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit scenario
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={e => {
                              e.stopPropagation()
                              handleDuplicate(s)
                            }}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate scenario
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={e => {
                              e.stopPropagation()
                              handleDelete(s)
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete scenario
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <CreateScenarioModal
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onSelect={handleSelectTemplate}
        />
      </div>
    </div>
  )
}
